import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
} from '@mui/material';
import { DataGridPremium, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import SearchIcon from '@mui/icons-material/Search';
import { getServiceForEntity } from '@/services/serviceRegistry';

export interface EntitySearchModalProps {
  entity: string;
  module: string;
  open: boolean;
  onClose: () => void;
  onSelect: (item: any) => void;
  searchFields: string[];
  displayField: string;
  additionalFields?: string[];
  /** lookup.columns: fields of the entity shown as columns after the display column. */
  columns?: string[];
}

export function EntitySearchModal({
  entity,
  module,
  open,
  onClose,
  onSelect,
  searchFields,
  displayField,
  additionalFields = [],
  columns: lookupColumns = [],
}: EntitySearchModalProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set([]),
  });
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  // Load items
  useEffect(() => {
    if (open) {
      loadItems();
    }
  }, [open, searchQuery, paginationModel]);

  const loadItems = async () => {
    try {
      setLoading(true);
      // Get the service based on entity
      const service = await getServiceForEntity(module, entity);
      
      // Call the service getAll
      const allItems = await service.getAll();
      
      // Filter items based on search query
      let filtered = allItems;
      if (searchQuery) {
        filtered = allItems.filter((item: any) =>
          searchFields.some(field =>
            item[field]?.toString().toLowerCase().includes(searchQuery.toLowerCase())
          )
        );
      }
      
      setItems(filtered);
    } catch (err) {
      console.error(`Failed to load ${entity}:`, err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Generate columns from fields — each headed by the entity's own label for the field.
  const header = (field: string) =>
    t(`DisplayName:${entity}.${field}`, { defaultValue: field.charAt(0).toUpperCase() + field.slice(1) });
  const columns: GridColDef[] = [
    {
      field: displayField,
      headerName: header(displayField),
      flex: 1,
      minWidth: 200,
    },
    ...lookupColumns.map(field => ({
      field,
      headerName: header(field),
      minWidth: 120,
    })),
    ...additionalFields.map(field => ({
      field,
      headerName: header(field),
      flex: 1,
      minWidth: 150,
    })),
  ];

  const handleSelect = () => {
    if (selectionModel.ids.size > 0) {
      const firstId = [...selectionModel.ids][0];
      const selectedItem = items.find(item => item.id === firstId);
      if (selectedItem) {
        onSelect(selectedItem);
        onClose();
      }
    }
  };

  const handleClose = () => {
    setSelectionModel({ type: 'include', ids: new Set() });
    setSearchQuery('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SearchIcon />
          <Typography variant="h6">{t('search.searchEntity', { entity })}</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            placeholder={t('search.searchBy', { fields: searchFields.join(', ') })}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
            }}
          />
        </Box>
        
        <Box sx={{ height: 'calc(80vh - 200px)' }}>
          <DataGridPremium
            rows={items}
            columns={columns}
            loading={loading}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            checkboxSelection
            disableMultipleRowSelection
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={setSelectionModel}
            onRowDoubleClick={(params) => {
              onSelect(params.row);
              onClose();
            }}
            sx={{
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
              },
            }}
          />
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose}>{t('common.cancel')}</Button>
        <Button
          onClick={handleSelect}
          variant="contained"
          disabled={selectionModel.ids.size === 0}
        >
          {t('common.select')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

